import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, CircularProgress, FormControl, MenuItem, Select, Typography,
} from '@mui/material';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';

const SYSTEM_ID = String(process.env.REACT_APP_SYSTEM_ID || '').replace(/^["']|["']$/g, '').trim();
const IS_MISHTAMERET = SYSTEM_ID === 'waiting_client';

const RANGES = [
  { id: '7d', label: 'שבוע' },
  { id: '30d', label: '30 יום' },
  { id: '90d', label: '3 חודשים' },
  { id: '1y', label: 'שנה' },
  { id: 'all', label: 'הכל' },
];

const PIE_COLORS = ['#c9a84c', '#4f8ef5', '#5bb38a', '#e07a5f', '#7c6bc4', '#3d8bfd', '#f2a65a', '#6c757d'];

const GOLD = '#c9a84c';
const NAVY = '#1a2744';
const CARD = {
  bgcolor: '#fff',
  borderRadius: 4,
  border: '1px solid #f1f5f9',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
  p: 2.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
};

function formatDateTime(value) {
  return formatHebrewDateTime(value);
}

function EmptyChart({ message = 'אין נתונים' }) {
  return (
    <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', textAlign: 'center', py: 10 }}>
      {message}
    </Typography>
  );
}

function KpiCard({ icon: Icon, title, children }) {
  return (
    <Box sx={CARD}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748b', fontSize: '0.8rem', fontWeight: 700, letterSpacing: 0.4 }}>
        <Icon sx={{ fontSize: 18, color: GOLD }} />
        {title}
      </Box>
      {children}
    </Box>
  );
}

function TrendCard({ title, data, color = NAVY, valueSuffix = '' }) {
  const hasData = (data || []).length > 0;
  return (
    <Box sx={{ ...CARD, minHeight: 220 }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#334155' }}>{title}</Typography>
      {!hasData ? (
        <EmptyChart />
      ) : (
        <Box sx={{ width: '100%', height: 160, direction: 'rtl' }}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={36} />
              <Tooltip
                formatter={(v) => [`${v}${valueSuffix}`, '']}
                contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
              <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}

export default function HomeDashboard() {
  const [range, setRange] = useState('30d');
  const [examId, setExamId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [units, setUnits] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { range };
      if (examId) params.examId = examId;
      const { data: payload } = await api.get('/reports/home-dashboard', { params });
      setData(payload);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בטעינת דף הבית');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, examId]);

  useEffect(() => {
    load();
  }, [load]);

  // בקשה נפרדת — תקלה בימות לא מפילה את דף הבית
  useEffect(() => {
    let cancelled = false;
    api.get('/reports/yemot-units')
      .then(({ data: payload }) => { if (!cancelled) setUnits({ value: payload.units }); })
      .catch(() => { if (!cancelled) setUnits({ failed: true }); });
    return () => { cancelled = true; };
  }, []);

  const resultsLink = useMemo(
    () => (IS_MISHTAMERET || data?.mode === 'mishtameret' ? '/american-exams' : '/reports'),
    [data?.mode]
  );

  const cityData = data?.cityBreakdown || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} dir="rtl">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>דף הבית</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8', mt: 0.25 }}>
            סקירה כללית של המערכת
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            p: 0.5,
            boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
          }}
        >
          {RANGES.map((r) => {
            const active = range === r.id;
            return (
              <Box
                key={r.id}
                component="button"
                type="button"
                onClick={() => setRange(r.id)}
                sx={{
                  appearance: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  px: 1.5,
                  py: 0.9,
                  borderRadius: 2,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  bgcolor: active ? NAVY : 'transparent',
                  color: active ? '#fff' : '#64748b',
                  transition: 'background 0.15s, color 0.15s',
                  '&:hover': { color: active ? '#fff' : '#334155' },
                }}
              >
                {r.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {error && (
        <Typography sx={{ color: 'error.main', fontSize: '0.9rem' }}>{error}</Typography>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : data ? (
        <>
          {/* KPI row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
              gap: 2,
              opacity: loading ? 0.6 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            <KpiCard icon={PeopleOutlinedIcon} title="נבחנים">
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: '2.25rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                    {data.members?.total ?? 0}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>סה״כ במערכת</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#16a34a' }}>
                    {data.members?.active ?? 0} פעילים
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#f59e0b' }}>
                    {data.members?.inactive ?? 0} לא פעילים
                  </Typography>
                </Box>
              </Box>
            </KpiCard>

            <KpiCard icon={RouteOutlinedIcon} title="מסלולים">
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: '2.25rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                    {data.tracksSummary?.activeTracks ?? 0}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>מסלולים פעילים</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.875rem', color: '#475569' }}>
                    {data.tracksSummary?.examsCompleted ?? 0} מבחנים מולאו
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#64748b' }}>
                    ע&quot;י {data.tracksSummary?.uniqueExaminees ?? 0} נבחנים
                  </Typography>
                </Box>
              </Box>
            </KpiCard>

            <KpiCard icon={PhoneInTalkOutlinedIcon} title="מערכת טלפונית — דקות האזנה">
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  columnGap: 2,
                  rowGap: 1,
                }}
              >
                {[
                  ['היום', data.ivrMinutes?.today],
                  ['7 ימים', data.ivrMinutes?.days7],
                  ['30 יום', data.ivrMinutes?.days30],
                  ['3 חודשים', data.ivrMinutes?.months3],
                  ['השנה', data.ivrMinutes?.year],
                  ['סה״כ', data.ivrMinutes?.total],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#334155' }}>
                      {value ?? 0} דק&apos;
                    </Typography>
                  </Box>
                ))}
              </Box>
              {data.ivrMinutesMeta && data.ivrMinutesMeta.connected === false && (
                <Typography sx={{ fontSize: '0.75rem', color: '#dc2626', mt: 0.5 }}>
                  לא ניתן לטעון מימות{data.ivrMinutesMeta.error ? ` — ${data.ivrMinutesMeta.error}` : ''}
                </Typography>
              )}
              {units && (
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', mt: 1 }}>
                  יתרת יחידות: {units.failed ? 'לא זמין' : units.value}
                </Typography>
              )}
            </KpiCard>
          </Box>

          {/* Middle row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <Box sx={{ ...CARD, gap: 2 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#334155' }}>סטטוס מסלולים</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>
                      {['מסלול', "מבחנ'", 'מולאו', 'נבחנים', 'ממוצע'].map((h, i) => (
                        <Box
                          key={h}
                          component="th"
                          sx={{
                            pb: 1,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            textAlign: i === 0 ? 'right' : 'center',
                            width: i === 0 ? 'auto' : 56,
                          }}
                        >
                          {h}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {(data.tracks || []).map((t) => (
                      <Box
                        key={t.key}
                        component="tr"
                        sx={{ borderBottom: '1px solid #f8fafc', '&:last-child': { borderBottom: 0 } }}
                      >
                        <Box component="td" sx={{ py: 1, fontWeight: 600, color: '#334155', fontSize: '0.75rem' }}>
                          {t.label}
                        </Box>
                        <Box component="td" sx={{ py: 1, textAlign: 'center', color: '#64748b', fontSize: '0.75rem' }}>
                          {t.examCount}
                        </Box>
                        <Box component="td" sx={{ py: 1, textAlign: 'center', fontSize: '0.75rem', color: t.completed ? '#334155' : '#cbd5e1', fontWeight: 600 }}>
                          {t.completed || 0}
                        </Box>
                        <Box component="td" sx={{ py: 1, textAlign: 'center', fontSize: '0.75rem', color: t.examinees ? '#334155' : '#cbd5e1', fontWeight: 600 }}>
                          {t.examinees || 0}
                        </Box>
                        <Box component="td" sx={{ py: 1, textAlign: 'center', fontSize: '0.75rem', color: t.avgScore != null ? '#334155' : '#cbd5e1' }}>
                          {t.avgScore != null ? t.avgScore : '—'}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ ...CARD, gap: 2 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#334155' }}>מבחנים אחרונים</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {(data.recentGrades || []).length === 0 ? (
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center', py: 4 }}>
                    אין ציונים בטווח שנבחר
                  </Typography>
                ) : (
                  (data.recentGrades || []).map((g) => (
                    <Box
                      key={g.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                        borderBottom: '1px solid #f8fafc',
                        '&:last-of-type': { borderBottom: 0 },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155' }} noWrap>
                          {g.name}
                          {g.city ? (
                            <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}>
                              {' '}· {g.city}
                            </Box>
                          ) : null}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }} noWrap>
                          {[g.examMeta, g.examName].filter(Boolean).join(' · ')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>
                          {formatDateTime(g.enteredAt)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 800, color: GOLD, fontSize: '0.875rem', ms: 1, flexShrink: 0 }}>
                        {g.scorePercent != null ? `${g.scorePercent}%` : '—'}
                      </Typography>
                    </Box>
                  ))
                )}
                <Box sx={{ pt: 1 }}>
                  <Typography
                    component={RouterLink}
                    to={resultsLink}
                    sx={{
                      fontSize: '0.75rem',
                      color: '#94a3b8',
                      textDecoration: 'none',
                      '&:hover': { color: NAVY },
                    }}
                  >
                    לכל המבחנים ←
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ ...CARD, gap: 2 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#334155' }}>פילוח לפי עיר</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  displayEmpty
                  value={examId}
                  onChange={(e) => setExamId(e.target.value)}
                  sx={{
                    borderRadius: 3,
                    fontSize: '0.75rem',
                    bgcolor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                  }}
                >
                  <MenuItem value="">
                    <em>בחר מבחן...</em>
                  </MenuItem>
                  {(data.examOptions || []).map((opt) => (
                    <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: '0.8rem' }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {cityData.length === 0 ? (
                <EmptyChart />
              ) : (
                <>
                  <Box sx={{ width: '100%', height: 160 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={cityData}
                          dataKey="count"
                          nameKey="city"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                        >
                          {cityData.map((entry, i) => (
                            <Cell key={entry.city} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#fff" />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v, name) => [v, name]}
                          contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                    {cityData.map((entry, i) => (
                      <Box key={entry.city} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: PIE_COLORS[i % PIE_COLORS.length],
                            flexShrink: 0,
                          }}
                        />
                        <Typography component="span" sx={{ color: '#475569', fontSize: '0.75rem' }}>
                          {entry.city}
                        </Typography>
                        <Typography component="span" sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                          ({entry.count})
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* Bottom charts */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <TrendCard title="נבחנים לאורך זמן" data={data.membersOverTime} color={NAVY} />
            <TrendCard title="ממוצע ציונים" data={data.avgScoresOverTime} color={GOLD} />
            <TrendCard title="מגמת דקות IVR" data={data.ivrTrend} color="#4f8ef5" valueSuffix=" דק'" />
          </Box>
        </>
      ) : null}
    </Box>
  );
}
